import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useUploadWithProgress } from '~/hooks/use-upload-with-progress'
import { moduleUploadError } from '~/pages/backoffice/admin/modules/module-upload-error'
import { useDescribeModuleUploadError } from '~/pages/backoffice/admin/modules/use-describe-module-upload-error'

/**
 * L'import d'un package SCORM : titre, fichier, envoi suivi, erreur (#310).
 *
 * Un échec garde le fichier et le titre : on corrige ou on réessaie sans tout
 * reprendre. Toute réponse de l'API rafraîchit la liste et le quota — un
 * package refusé à l'extraction y reste listé en échec.
 */
export function useModuleImport(onImported: () => Promise<unknown>) {
  const { t } = useTranslation()
  const { state, upload, cancel } = useUploadWithProgress()
  const [title, setTitle] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const describe = useDescribeModuleUploadError()

  const chooseFile = (chosen: File | null) => {
    setFile(chosen)
    setError(null)
  }

  const submit = async () => {
    if (!file) {
      setError(t('modules.selectFile'))
      return
    }
    setError(null)
    const form = new FormData()
    form.append('title', title)
    form.append('file', file)

    const outcome = await upload('/modules/upload', form)
    // Annulé par « Annuler », ou en quittant l'écran pendant l'envoi : dans
    // les deux cas rien n'est parti, et le dire évite de croire le module importé.
    if (outcome.kind === 'cancelled') {
      toast(t('modules.upload.cancelled'))
      return
    }
    const cause = moduleUploadError(outcome)
    if (cause) {
      setError(describe(cause))
    } else {
      toast.success(t('modules.imported'))
      setTitle('')
      setFile(null)
    }
    // Le module est importé (ou listé en échec) même si la liste ne se
    // rafraîchit pas : on le dit, sans repasser l'import en échec.
    if (outcome.kind === 'response') await onImported().catch(() => toast.error(t('modules.refreshFailed')))
  }

  return { title, setTitle, file, chooseFile, error, state, submit, cancel, busy: state.phase !== 'idle' }
}
